// Cvanto AI — the persistent assistant space (route /ai and /ai/:conversationId).
// A real chat with history, not a modal: conversations on the left (a drawer on
// phones), the active thread on the right. Marketplace results inside the
// thread are structured UI rendered from backend data; the assistant's own
// text is short and built by the backend from real counts.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CvantoAIVisual from "../SearchResults/CvantoAIVisual";
import ConversationList from "./ConversationList";
import MessageBubble, { AiMark } from "./MessageBubble";
import Composer from "./Composer";
import useCvantoChat from "./useCvantoChat";
import "./CvantoAI.scss";

const SUGGESTIONS = [
  "Чорна куртка до 4000 грн",
  "Кросівки Nike 43 розмір",
  "Манікюр у Львові",
  "iPhone 13 до 20 000 грн",
];

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 7h14M5 12h14M5 17h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ComposeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 20l4.2-1 10-10a2 2 0 0 0-2.8-2.8l-10 10L4 20zM14.5 7.5l2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function CvantoAIPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const chat = useCvantoChat({
    conversationId,
    // The first message creates the conversation; move the URL to it without
    // adding a history entry (Back goes to where the user came from).
    onConversationCreated: (id) => navigate(`/ai/${id}`, { replace: true }),
    onConversationMissing: () => navigate("/ai", { replace: true }),
  });

  const {
    conversations, conversationsLoading, conversationsError, hasMoreConversations, loadConversations, loadMoreConversations,
    currentId, messages, historyLoading, historyError, hasMoreHistory, loadOlder, reloadHistory,
    sending, usage, send, sendAction, retry,
    startNewConversation, removeConversation, removeAllConversations, rename,
  } = chat;

  const scrollRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastMessage = messages[messages.length - 1];

  // Follow new content while the user is at the bottom; leave them alone when
  // they scrolled up to read older messages.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96;
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages.length, lastMessage?.text, lastMessage?.results, lastMessage?.phase]);

  useEffect(() => {
    stickToBottomRef.current = true;
  }, [currentId]);

  const handleSend = useCallback(
    (text) => {
      stickToBottomRef.current = true;
      return send(text);
    },
    [send]
  );

  const openConversation = (id) => {
    setDrawerOpen(false);
    navigate(`/ai/${id}`);
  };

  const newConversation = () => {
    setDrawerOpen(false);
    startNewConversation();
    navigate("/ai");
  };

  const remaining = usage?.remaining;
  const empty = !historyLoading && !historyError && messages.length === 0;

  return (
    <div className="cvai">
      <div className="cvai__shell">
      <div className={`cvai__list-pane ${drawerOpen ? "is-open" : ""}`}>
        <ConversationList
          conversations={conversations}
          loading={conversationsLoading}
          error={conversationsError}
          hasMore={hasMoreConversations}
          activeId={currentId}
          onOpen={openConversation}
          onNew={newConversation}
          onDelete={async (id) => {
            await removeConversation(id);
            if (id === conversationId) navigate("/ai", { replace: true });
          }}
          onDeleteAll={async () => {
            await removeAllConversations();
            navigate("/ai", { replace: true });
          }}
          onRename={rename}
          onRetry={() => loadConversations()}
          onLoadMore={loadMoreConversations}
        />
      </div>
      {drawerOpen && <button type="button" className="cvai__scrim" aria-label="Закрити список розмов" onClick={() => setDrawerOpen(false)} />}

      <section className="cvai__main" aria-label="Чат Cvanto AI">
        <header className="cvai__header">
          <button type="button" className="cvai__icon-btn cvai__back" onClick={() => navigate(-1)} aria-label="Назад">
            <BackIcon />
          </button>
          <button type="button" className="cvai__icon-btn cvai__drawer-btn" onClick={() => setDrawerOpen(true)} aria-label="Розмови">
            <ListIcon />
          </button>
          <AiMark />
          <h1 className="cvai__title">
            {conversations.find((c) => c.id === currentId)?.title || "Cvanto AI"}
          </h1>
          <button type="button" className="cvai__icon-btn" onClick={newConversation} aria-label="Нова розмова">
            <ComposeIcon />
          </button>
        </header>

        <div className="cvai__scroll" ref={scrollRef} onScroll={onScroll} aria-live="polite" aria-busy={sending}>
          {hasMoreHistory && (
            <button type="button" className="cvai-link-btn cvai__older" onClick={loadOlder} disabled={historyLoading}>
              Показати попередні повідомлення
            </button>
          )}

          {historyLoading && messages.length === 0 && (
            <div className="cvai__skeletons" aria-hidden="true">
              <div className="cvai__sk cvai__sk--user" />
              <div className="cvai__sk cvai__sk--ai" />
              <div className="cvai__sk cvai__sk--ai cvai__sk--short" />
            </div>
          )}

          {historyError && (
            <div className="cvai-error cvai__history-error" role="alert">
              <span>{historyError}</span>
              <button type="button" className="cvai-link-btn" onClick={reloadHistory}>
                Спробувати ще раз
              </button>
            </div>
          )}

          {empty && (
            <div className="cvai__empty">
              <CvantoAIVisual state="idle" decorative />
              <h2 className="cvai__empty-title">Що шукаємо?</h2>
              <p className="cvai__empty-sub">
                Напишіть своїми словами — товар, послугу чи магазин. Я знайду це серед реальних оголошень Cvanto.
              </p>
              <div className="cvai__chips">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="cvai__chip" disabled={sending} onClick={() => handleSend(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              message={m}
              isLast={i === messages.length - 1}
              sending={sending}
              onAction={sendAction}
              onRetry={retry}
            />
          ))}
        </div>

        <Composer onSend={handleSend} disabled={sending} remaining={remaining} autoFocus />
      </section>
      </div>
    </div>
  );
}
